// src/components/grammar/GrammarPillarSelector.jsx - FIXED VERSION
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  PlayCircle,
  TrendingUp,
  Lock,
  ArrowUpRight
} from 'lucide-react';

const ELEVATED_SURFACE =
  'relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.06)]';

const ExercisePreview = ({ exercise }) => {
  const shuffledWords = useMemo(() => {
    if (!exercise?.stimulus || exercise.exerciseType !== 'sentenceBuilding') return null;
    const words = [...exercise.stimulus];
    // Deterministic visual shuffle: rotate by half
    const mid = Math.ceil(words.length / 2);
    return [...words.slice(mid), ...words.slice(0, mid)];
  }, [exercise?.id]);

  if (!exercise) return null;

  const typeLabel = {
    sentenceBuilding: 'Build a sentence',
    multipleChoice: 'Multiple choice',
    matchingPairs: 'Match pairs',
    errorCorrection: 'Fix the error',
    fillInTheBlanks: 'Fill in blanks',
    sentenceTransformation: 'Transform sentence',
  }[exercise.exerciseType] || exercise.exerciseType;

  return (
    <div className="exercise-preview-section mt-4 pointer-events-none select-none">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] font-bold tracking-widest uppercase text-black/40">Sample</span>
        <span className="text-[10px] font-bold tracking-widest uppercase text-black/40">·</span>
        <span className="text-[10px] font-bold tracking-widest uppercase text-black/40">{typeLabel}</span>
      </div>

      {exercise.exerciseType === 'sentenceBuilding' && (
        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Arrange the words:</p>
          <div className="flex flex-wrap gap-1.5">
            {(shuffledWords || exercise.stimulus).map((word, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-white border-2 border-black rounded-lg text-xs font-medium text-black"
              >
                {word}
              </span>
            ))}
          </div>
        </div>
      )}

      {exercise.exerciseType === 'multipleChoice' && (
        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-xs text-gray-700 leading-snug mb-2 line-clamp-2">
            {exercise.stimulus?.replace(/(__+)|(\[---\])/g, '______')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {(exercise.options || []).map((opt, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-white border border-gray-300 rounded-lg text-xs text-gray-700"
              >
                {opt}
              </span>
            ))}
          </div>
        </div>
      )}

      {exercise.exerciseType === 'matchingPairs' && (
        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Match the pairs:</p>
          <div className="grid grid-cols-2 gap-1.5">
            {(exercise.correctAnswer || []).slice(0, 3).map((pair, i) => (
              <React.Fragment key={i}>
                <span className="px-2 py-1 bg-white border border-gray-300 rounded-lg text-xs text-gray-700 text-center truncate">{pair.left}</span>
                <span className="px-2 py-1 bg-white border border-gray-300 rounded-lg text-xs text-gray-700 text-center truncate">{pair.right}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {exercise.exerciseType === 'errorCorrection' && (
        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-xs text-gray-500 mb-2">Find &amp; fix the error:</p>
          <p className="text-xs text-gray-700 leading-snug line-clamp-2">
            {exercise.stimulus?.replace(
              exercise.incorrectPart,
              `[${exercise.incorrectPart}]`
            )}
          </p>
        </div>
      )}

      {exercise.exerciseType === 'fillInTheBlanks' && (
        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-xs text-gray-700 leading-snug line-clamp-3">
            {typeof exercise.stimulus === 'string'
              ? exercise.stimulus.replace(/(__+)|(\[---\])/g, (m) => '______')
              : exercise.question}
          </p>
        </div>
      )}

      {exercise.exerciseType === 'sentenceTransformation' && (
        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-xs text-gray-500 mb-1">Rewrite:</p>
          <p className="text-xs text-gray-700 leading-snug line-clamp-2">{exercise.stimulus || exercise.question}</p>
        </div>
      )}
    </div>
  );
};

const GrammarPillarSelector = ({
  level,
  userProgress,
  pillarConnections,
  onSelectPillar,
  onGoToLesson,
  onBack,
  subscriptionStatus,
  navigate
}) => {
  const { t } = useTranslation();
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [blockedPillarName, setBlockedPillarName] = useState(null);

  const getPillarProgress = (pillarId, totalExerciseCount = 0) => {
    const pillarProgress = userProgress?.pillars?.[pillarId];
    if (!pillarProgress) return { completed: 0, total: totalExerciseCount, percentage: 0 };

    const exercises = Object.values(pillarProgress.exercises || {});
    const completed = exercises.filter(e => e.completed).length;
    // Use the real exercise count from the JSON data as the denominator,
    // falling back to the number of touched exercises if somehow missing.
    const total = totalExerciseCount > 0 ? totalExerciseCount : exercises.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  };

  const sortedPillars = Object.values(pillarConnections).sort((a, b) => {
    if (a.isConnected !== b.isConnected) return b.isConnected ? 1 : -1;
    const na = Number(a.pillarNumber ?? a.lessonId ?? a.id);
    const nb = Number(b.pillarNumber ?? b.lessonId ?? b.id);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return 0;
  });

  const getPillarNumId = (pillar) => pillar.pillarNumber ?? pillar.lessonId ?? pillar.id;

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 py-2 sm:py-8">
       {/* Header - Compact on Mobile, removed redundant title since it's in main header */}
<div className="text-center mb-2 sm:mb-8 px-2">
  {/* Only show badge on mobile, desktop header already shows level */}
  <div className="inline-block px-2.5 sm:px-4 py-1 sm:py-2 bg-black text-white text-[10px] sm:text-sm font-bold tracking-widest uppercase rounded-full mb-1 sm:mb-4 sm:hidden">
    {level}
  </div>
  {/* Hide title on mobile since it's already in main header, show on desktop */}
  <h1 className="font-space text-lg sm:text-5xl md:text-6xl font-black text-black mb-1 sm:mb-4 tracking-tight leading-tight px-4 hidden sm:block">
    {t('grammar.pillars.title', { level })}
  </h1>
  <p className="text-xs sm:text-xl text-gray-600 max-w-3xl mx-auto hidden sm:block px-4">
    {t('grammar.subtitle')}
  </p>
</div>

        {/* Pillars Grid - Mobile Optimized */}
        <div className="grammar-mobile-single-col grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-6">
        {sortedPillars.map((pillar, index) => {
  const pillarNumId = getPillarNumId(pillar);
  const progress = getPillarProgress(pillarNumId, pillar.exerciseCount);
  const isStarted = progress.completed > 0;
  const isLocked = subscriptionStatus?.isFree && pillarNumId !== 1 && pillarNumId !== '1';

  return (
    <div
      key={pillarNumId}
      onClick={() => {
        if (isLocked) {
          setBlockedPillarName(pillar.title);
          setShowPremiumModal(true);
        } else if (pillar.hasPractice) {
          onSelectPillar(pillarNumId);
        }
      }}
      className={`grammar-pillar-card rounded-lg sm:rounded-3xl border text-black shadow-[0_4px_20px_rgba(0,0,0,0.05)] ${isLocked ? 'border-[#006D77] bg-gradient-to-br from-white via-[#f2fbf9] to-[#e7f7f4] shadow-[0_8px_26px_rgba(0,109,119,0.18)]' : 'border-[#E1E4E8] bg-white'} ${pillar.hasPractice ? 'cursor-pointer' : ''}`}
    >
      {/* Desktop: Vertical layout */}
      <div className="hidden sm:block p-8 relative" style={{ borderWidth: '1px', borderColor: isLocked ? '#006D77' : '#E1E4E8' }}>
        {isLocked && (
          <div className="absolute top-4 left-4 z-20 inline-flex items-center rounded-full border border-[#006D77]/30 bg-white/95 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#006D77]">
            {t('premium.only')}
          </div>
        )}
        {/* Number Badge in Background - GRAY */}
        <div className="grammar-number-bg absolute top-4 right-6 font-space text-[4rem] font-black text-gray-200 opacity-50 leading-none">
          {String(index + 1).padStart(2, '0')}
        </div>

        {/* Decorative Dots - ROYAL BLUE (accent) */}
        <div className="grammar-decorative-dot grammar-dot-1"></div>
        <div className="grammar-decorative-dot grammar-dot-2"></div>
        <div className="grammar-decorative-dot grammar-dot-3"></div>

        {/* Shine Effect */}
        <div className="grammar-shine-effect"></div>

        {/* Icon - BookOpen for all */}
        <div
          className="grammar-icon-animated relative w-[70px] h-[70px] rounded-2xl flex items-center justify-center text-white text-3xl mb-6 mx-auto"
          style={{ backgroundColor: isLocked ? '#006D77' : '#000000' }}
        >
          <BookOpen className="w-10 h-10" />
        </div>

        {/* Title - BLACK text */}
        <h3 className="grammar-title-animated font-space text-2xl font-black text-black text-center mb-2 leading-tight">
          {pillar.title}
        </h3>

        {/* Description - GRAY text */}
        {pillar.description && (
          <p className="text-sm text-center text-gray-600 mb-4 transition-colors duration-500 line-clamp-2">
            {pillar.description}
          </p>
        )}

        {/* Exercise Preview */}
        {pillar.hasPractice && !isLocked && (
          <ExercisePreview exercise={pillar.practiceData?.exercises?.[0]} />
        )}

        {/* Exercise Count - GRAY with icon */}
        {pillar.hasPractice && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4 mt-4">
            <PlayCircle className="w-4 h-4" />
            <span>{t('grammar.pillars.exercises', { count: pillar.exerciseCount })}</span>
          </div>
        )}

        {/* Progress Info - Gradient progress bar */}
        {isStarted && (
          <div className="grammar-progress-info mt-4 pt-4 border-t border-gray-200 transition-all duration-500">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600 transition-colors duration-500">
                {t('grammar.pillars.progress')}
              </span>
              <span className="font-medium text-black transition-colors duration-500">
                {progress.completed}/{progress.total}
              </span>
            </div>
            <div className="grammar-progress-bar w-full bg-gray-200 rounded-full h-2 transition-all duration-500">
              <div
                className="h-2 rounded-full transition-all duration-500"
                style={{
                  width: `${progress.percentage}%`,
                  background: 'linear-gradient(90deg, #000000 0%, #4a4a4a 100%)'
                }}
              />
            </div>
          </div>
        )}

        {/* Action Buttons - BLACK primary, WHITE secondary */}
        <div className="mt-4 flex gap-2">
          {pillar.hasLesson && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isLocked) {
                  setBlockedPillarName(pillar.title);
                  setShowPremiumModal(true);
                } else {
                  onGoToLesson(pillarNumId);
                }
              }}
              className="flex-1 py-2 px-3 bg-white text-black border-2 border-black rounded-lg text-sm font-semibold hover:bg-black hover:text-white transition-all flex items-center justify-center gap-1"
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden lg:inline">{t('grammar.pillars.linkedLesson')}</span>
            </button>
          )}
          {pillar.hasPractice && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isLocked) {
                  setBlockedPillarName(pillar.title);
                  setShowPremiumModal(true);
                } else {
                  onSelectPillar(pillarNumId);
                }
              }}
              className="flex-1 py-2 px-3 bg-[#006D77] text-white rounded-lg text-sm font-semibold hover:bg-[#005a63] transition-all flex items-center justify-center gap-1 shadow-[0_4px_20px_rgba(0,0,0,0.05)]"
            >
              <PlayCircle className="w-4 h-4" />
              <span>{t('grammar.pillars.startPractice')}</span>
            </button>
          )}
        </div>

        {/* Corner Decorations */}
        <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 transition-colors"></div>
        <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 transition-colors"></div>
      </div>

      {/* Mobile: Horizontal compact layout - Bento Box Architecture */}
      <div className="block sm:hidden p-2 relative">
        {isLocked && (
          <div className="absolute top-2 right-2 z-20 inline-flex items-center rounded-full border border-[#006D77]/30 bg-white/95 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#006D77]">
            {t('premium.only')}
          </div>
        )}
        <div className="flex items-center gap-2">
          {/* Decorative dots for mobile */}
          <div className="grammar-decorative-dot grammar-dot-1"></div>
          <div className="grammar-decorative-dot grammar-dot-2"></div>

          {/* Shine Effect */}
          <div className="grammar-shine-effect"></div>

          {/* Icon on left - 50px as specified */}
          <div
            className="grammar-icon-animated w-[50px] h-[50px] rounded-xl flex items-center justify-center text-white flex-shrink-0"
            style={{ backgroundColor: isLocked ? '#006D77' : '#000000' }}
          >
            <BookOpen className="w-6 h-6" />
          </div>

          {/* Content on right */}
          <div className="flex-1 min-w-0">
            <h3 className="grammar-title-animated font-space text-base font-black text-black mb-1 leading-tight line-clamp-2">
              {pillar.title}
            </h3>

            {/* Exercise count + type badge */}
            {pillar.hasPractice && (
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs text-gray-500 transition-colors duration-500">
                  {t('grammar.pillars.exercises', { count: pillar.exerciseCount })}
                </p>
                {!isLocked && pillar.practiceData?.exercises?.[0] && (
                  <span className="text-[10px] font-bold tracking-wide uppercase px-1.5 py-0.5 bg-black/5 rounded text-black/50">
                    {{
                      sentenceBuilding: 'Build',
                      multipleChoice: 'Choice',
                      matchingPairs: 'Match',
                      errorCorrection: 'Fix',
                      fillInTheBlanks: 'Fill',
                      sentenceTransformation: 'Transform',
                    }[pillar.practiceData.exercises[0].exerciseType] || 'Exercise'}
                  </span>
                )}
              </div>
            )}

            {/* Progress bar - full width */}
            {isStarted && (
              <div className="w-full mb-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-black">{progress.percentage}%</span>
                  <span className="text-[10px] text-gray-500">{progress.completed}/{progress.total}</span>
                </div>
                <div className="w-full h-1.5 rounded-full overflow-hidden bg-gray-200">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${progress.percentage}%`,
                      background: 'linear-gradient(90deg, #000000 0%, #4a4a4a 100%)'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Mobile Action Buttons - BLACK & WHITE */}
            <div className="flex gap-2 mt-2">
              {pillar.hasLesson && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isLocked) {
                      setBlockedPillarName(pillar.title);
                      setShowPremiumModal(true);
                    } else {
                      onGoToLesson(pillarNumId);
                    }
                  }}
                  className="flex-1 py-2 bg-white text-black border-2 border-black rounded-lg text-xs font-bold hover:bg-black hover:text-white transition-all"
                >
                  Lesson
                </button>
              )}
              {pillar.hasPractice && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isLocked) {
                      setBlockedPillarName(pillar.title);
                      setShowPremiumModal(true);
                    } else {
                      onSelectPillar(pillarNumId);
                    }
                  }}
                  className="flex-1 py-2 bg-[#006D77] text-white rounded-lg text-xs font-bold hover:bg-[#005a63] transition-all shadow-[0_4px_20px_rgba(0,0,0,0.05)]"
                >
                  Practice
                </button>
              )}
            </div>
          </div>

          {/* Corner decorations */}
          <div className="absolute top-1 left-1 w-2 h-2 border-t-2 border-l-2 border-black/20"></div>
          <div className="absolute bottom-1 right-1 w-2 h-2 border-b-2 border-r-2 border-black/20"></div>
        </div>
      </div>
    </div>
  );
})}
        </div>

        {/* Info Section - Hidden on Mobile */}
        <div className="mt-8 sm:mt-16 text-center hidden sm:block">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-black/5 rounded-full border border-black/10">
            <TrendingUp className="w-5 h-5 text-[#3A86FF]" />
            <span className="text-sm font-medium text-black/70">
              {t('grammar.progress.continueFromLast')}
            </span>
          </div>
        </div>

        {/* Premium Modal */}
        {showPremiumModal && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-fade-in">
            <div className={`${ELEVATED_SURFACE} max-w-sm w-full p-6 sm:p-8 animate-slide-up`}>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100">
                    <Lock className="w-7 h-7 text-[#006D77]" />
                  </div>
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">{t('premium.required.grammar.title', { defaultValue: 'Master German grammar with confidence' })}</h2>
                <p className="text-sm text-gray-600 mb-6">
                  {t('premium.required.grammar.body', { defaultValue: 'Advanced grammar lessons and exercises are available only for Premium subscribers. Upgrade now to unlock all grammar modules.' })}
                </p>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      navigate('/pricing');
                      setShowPremiumModal(false);
                    }}
                    className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl bg-gradient-to-r from-[#00474f] via-[#006D77] to-[#007f8a] text-white font-bold text-sm hover:shadow-[0_8px_20px_rgba(0,109,119,0.35)] active:scale-[0.98] touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#006D77]/40"
                  >
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                    {t('premium.required.grammar.cta', { defaultValue: 'Unlock grammar lessons' })}
                  </button>

                  <button
                    onClick={() => setShowPremiumModal(false)}
                    className="w-full min-h-[48px] inline-flex items-center justify-center px-4 py-3 rounded-xl border-2 border-black/10 bg-white text-gray-700 font-semibold text-sm hover:border-black/30 hover:bg-gray-50 touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40"
                  >
                    {t('premium.required.secondary')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrammarPillarSelector;